import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import Icon from './Icon.jsx';
import useFillViewport from '../hooks/useFillViewport.js';
import { copyToClipboard } from '../lib/daily.js';
import { drawHall, WALK_END } from '../lib/hall.js';
import { playCorrect, playWrong } from '../lib/sfx.js';
import { dealWalk, rankWalk, scoreWalk, shareWalk, WALK_LIVES, WALK_STEPS } from '../lib/theWalk.js';

/**
 * The Third Day.
 *
 * ── What it is ───────────────────────────────────────────────────────────────
 *
 * A quiz you walk. The screen is a dark Roman colonnade with a garden at the
 * far end of it, and every right answer moves the camera one bay closer. Get
 * one wrong and a soldier's pilum comes down across the way — you lose one of
 * three, and you do not advance.
 *
 * ── Why this scene ───────────────────────────────────────────────────────────
 *
 * The soldiers are Roman because scripture puts them exactly here: Pilate
 * gives the chief priests a guard for the tomb (Matthew 27:65–66), and it is
 * that guard the morning walks past. The questions are the easy, familiar ones
 * about Jesus on purpose — the mode is a walk, not an exam, and the reward is
 * the corridor filling with light rather than the difficulty of the questions.
 *
 * ── The measurable part ──────────────────────────────────────────────────────
 *
 * All the drawing lives in lib/hall.js. The only state the animation needs is
 * a camera position, so progress is genuinely visible rather than implied:
 * walking the camera down the colonnade takes the mean brightness at the
 * centre of the frame from about 39 to about 223.
 */

const STEP_MS = 900;
const BEAT_MS = 780;

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

export default function ThirdDay({
  best = 0,
  runs = 0,
  signedIn = false,
  soundOn = true,
  onNeedAccount,
  onUpgrade,
  onFinish,
  onExit,
}) {
  const [deck, setDeck] = useState(() => dealWalk());
  /*
   * Two counters, deliberately. `at` is steps taken toward the garden and only
   * moves on a right answer; `qi` is which question is on the board and moves
   * on every answer. Sharing one meant a wrong answer re-asked the question
   * whose answer had just been shown on the verse card — a free step, and the
   * reason the answer appeared to be given away in advance.
   */
  const [at, setAt] = useState(0);
  const [qi, setQi] = useState(0);
  // The question just answered, kept so the verse card can name it without
  // index arithmetic against a counter that has already moved on.
  const [asked, setAsked] = useState(null);
  const [lives, setLives] = useState(WALK_LIVES);
  const [phase, setPhase] = useState('intro'); // intro | ask | judged | between | arrive | over
  const [picked, setPicked] = useState(null);
  const [copied, setCopied] = useState(false);

  const sectionRef = useRef(null);
  const stageRef = useRef(null);
  const panelRef = useRef(null);
  const canvasRef = useRef(null);
  // The camera is a ref, not state: it changes every animation frame and
  // nothing in the DOM depends on it, so state would mean sixty React renders
  // a second to paint a canvas React does not own.
  const camRef = useRef({ z: 0, from: 0, to: 0, at: 0, bob: 0 });
  const barRef = useRef(0);
  const rafRef = useRef(0);
  const timersRef = useRef([]);

  const later = useCallback((fn, ms) => {
    const id = setTimeout(fn, ms);
    timersRef.current.push(id);
    return id;
  }, []);
  const clearTimers = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  }, []);
  useEffect(() => clearTimers, [clearTimers]);

  const round = deck[Math.min(qi, deck.length - 1)];
  const walking = phase === 'ask' || phase === 'judged' || phase === 'between';

  /*
   * Fit the corridor to the room actually available — see useFillViewport for
   * why the space has to be measured rather than expressed in vh.
   *
   * While walking the corridor takes all of it and the panel sits over it. On
   * the intro and the endings the panel is in flow and carries far more than
   * fits — verse, tally, the account ask, share, exit — so it gets a fixed
   * frame and scrolls inside it. Either way the page itself never scrolls,
   * which means the garden the player walked eight steps for stays on screen
   * instead of sitting below the fold.
   */
  useFillViewport(
    sectionRef,
    walking ? 'walk' : 'rest',
    (room) => {
      const section = sectionRef.current;
      const stage = stageRef.current;
      if (!section || !stage) return;
      // No upper cap while walking: the corridor is the screen now, and a cap
      // left 300px of empty app background below it on a large phone.
      const wanted = walking ? Math.max(180, room) : clamp(room * 0.44, 150, 420);
      section.style.height = walking ? '' : `${Math.round(Math.max(220, room))}px`;
      const current = parseFloat(stage.style.height) || 0;
      if (Math.abs(current - wanted) > 1) stage.style.height = `${Math.round(wanted)}px`;
    },
    [phase],
  );

  /* ── the frame loop ──────────────────────────────────────────────────────── */

  const frame = useCallback((now) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    const W = Math.max(1, Math.round(rect.width));
    const H = Math.max(1, Math.round(rect.height));
    if (canvas.width !== Math.round(W * dpr) || canvas.height !== Math.round(H * dpr)) {
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const cam = camRef.current;
    if (cam.at) {
      const t = clamp((now - cam.at) / STEP_MS, 0, 1);
      cam.z = lerp(cam.from, cam.to, easeInOut(t));
      // a step has a rise and a fall to it; a camera that only slides
      // forward feels like a lift, not like walking
      cam.bob = Math.sin(t * Math.PI * 2) * 0.035 * (1 - t);
      if (t >= 1) {
        cam.at = 0;
        cam.bob = 0;
      }
    }

    drawHall(ctx, W, H, { camZ: cam.z, bob: cam.bob, barAt: barRef.current, now });
    rafRef.current = requestAnimationFrame(frame);
  }, []);

  useEffect(() => {
    rafRef.current = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(rafRef.current);
  }, [frame]);

  /* ── the walk ────────────────────────────────────────────────────────────── */

  const walkTo = (step) => {
    const cam = camRef.current;
    cam.from = cam.z;
    cam.to = (step / WALK_STEPS) * WALK_END;
    cam.at = performance.now();
  };

  const begin = () => {
    clearTimers();
    camRef.current = { z: 0, from: 0, to: 0, at: 0, bob: 0 };
    barRef.current = 0;
    setDeck(dealWalk());
    setAt(0);
    setQi(0);
    setAsked(null);
    setLives(WALK_LIVES);
    setPicked(null);
    setPhase('ask');
  };

  const answer = (index) => {
    if (phase !== 'ask') return;
    const ok = round.options[index].ok;
    setPicked(index);
    setAsked({ question: round, right: ok });
    setPhase('judged');
    if (soundOn) (ok ? playCorrect : playWrong)();

    /*
     * The question does not change until the verdict beat is over.
     *
     * Advancing it here instead re-rendered the board mid-beat with the *next*
     * question's options while `picked` still pointed at the old list — so the
     * question appeared to change under the player's finger and a correct
     * answer was marked against the wrong options and shown as wrong.
     */
    if (ok) {
      const next = at + 1;
      walkTo(next);
      later(() => {
        setAt(next);
        setQi((n) => n + 1);
        if (next >= WALK_STEPS) {
          setPhase('arrive');
          onFinish?.(scoreWalk(next, lives), { steps: next, lives, reached: true });
        } else {
          setPhase('between');
        }
      }, BEAT_MS);
    } else {
      const left = lives - 1;
      setLives(left);
      barRef.current = performance.now();
      later(() => {
        setQi((n) => n + 1);
        if (left <= 0) {
          setPhase('over');
          onFinish?.(scoreWalk(at, 0), { steps: at, lives: 0, reached: false });
        } else {
          setPhase('between');
        }
      }, BEAT_MS);
    }
  };

  // One complete walk, then an account. `runs` already counts the walk that
  // has just ended, so this is true on the first ending an anonymous player
  // sees — after they have played, never before.
  const gate = !signedIn && runs >= 1;
  const score = scoreWalk(phase === 'arrive' ? WALK_STEPS : at, phase === 'arrive' ? lives : 0);
  const shareable = useMemo(
    () => shareWalk(phase === 'arrive' ? WALK_STEPS : at, phase === 'arrive' ? lives : 0, score),
    [phase, at, lives, score],
  );

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text: shareable });
        return;
      } catch {
        /* dismissed — fall through to copy */
      }
    }
    if (await copyToClipboard(shareable)) {
      setCopied(true);
      later(() => setCopied(false), 2000);
    }
  };

  /* ── panels ──────────────────────────────────────────────────────────────── */

  const wasRight = asked?.right ?? false;

  let panel;
  if (phase === 'intro') {
    panel = (
      <>
        <p className="td__eyebrow">Jerusalem · before first light</p>
        <h2 className="td__title">The third day</h2>
        <p className="td__scripture">
          Pilate said to them, “You have a guard. Go, make it as secure as you can.” So they went
          with the guard and made the tomb secure, sealing the stone.
        </p>
        <p className="td__ref">Matthew 27:65–66</p>
        <p className="td__scripture td__scripture--small">
          Now after the Sabbath, as it began to dawn on the first day of the week, Mary Magdalene and
          the other Mary came to see the tomb.
        </p>
        <p className="td__ref">Matthew 28:1</p>
        <p className="td__narrate">
          The way is long and it is lined with Rome. At the far end of it there is a garden, and it
          is already light.
        </p>
        <button type="button" className="btn btn--primary btn--block btn--lg" onClick={begin}>
          Walk toward the light
        </button>
        {/* The tab bar is hidden while a game is open, so every screen needs
            its own way out. The walk itself has the X in the corner. */}
        <button type="button" className="btn btn--ghost btn--block" onClick={onExit}>
          Back to practice
        </button>
      </>
    );
  } else if (phase === 'ask' || phase === 'judged') {
    // Read the board from the question that was answered while the verdict is
    // up, never from a cursor that may have moved on.
    const board = phase === 'judged' && asked ? asked.question : round;
    panel = (
      <>
        <p className="td__eyebrow">
          {WALK_STEPS - at} step{WALK_STEPS - at === 1 ? '' : 's'} to the garden
        </p>
        <p className="td__ask">{board.q}</p>
        {/* Short answers pair up two to a row. Four full-width buttons make the
            panel tall enough to cover the corridor, which is the one thing the
            player is meant to be looking at. Long answers stay one per row —
            "Love the Lord your God with all your heart" does not fit half a
            phone. */}
        <div
          className={`td__opts${
            board.options.every((o) => o.text.length <= 24) ? ' td__opts--pairs' : ''
          }`}
        >
          {board.options.map((o, i) => (
            <button
              key={o.text}
              type="button"
              className={`td__opt${
                phase === 'judged' ? (o.ok ? ' is-right' : i === picked ? ' is-wrong' : '') : ''
              }`}
              disabled={phase !== 'ask'}
              onClick={() => answer(i)}
            >
              {o.text}
            </button>
          ))}
        </div>
      </>
    );
  } else if (phase === 'between') {
    const just = asked.question;
    /*
     * Name the question and its answer outright.
     *
     * The verse card follows a question that is now gone from the board, and
     * the next one asked is a different one — so a card that only quotes
     * scripture leaves the player working out what it was even about. It says
     * what was asked and what the answer was, and only then why.
     */
    const correct = just.options.find((o) => o.ok)?.text;
    panel = (
      <>
        <p className="td__eyebrow">
          {wasRight ? 'A step closer' : 'A spear comes down across the way'}
        </p>
        <p className="td__recap">{just.q}</p>
        <p className={`td__answer${wasRight ? '' : ' is-missed'}`}>
          <Icon name={wasRight ? 'check' : 'close'} size={15} />
          <span>
            {correct}
            {wasRight ? null : <em> — that was the answer</em>}
          </span>
        </p>
        <p className="td__scripture td__scripture--small">{just.note}</p>
        <p className="td__ref">{just.ref}</p>
        <button
          type="button"
          className={`btn btn--block btn--lg ${wasRight ? 'btn--leaf' : 'btn--primary'}`}
          onClick={() => {
            setPicked(null);
            setPhase('ask');
          }}
        >
          {wasRight ? 'Keep walking' : 'Stand your ground'}
        </button>
      </>
    );
  } else if (phase === 'arrive') {
    panel = (
      <>
        <p className="td__eyebrow">The stone is rolled away</p>
        <h2 className="td__title td__title--leaf">The garden</h2>
        {/* Verse 6 alone. The full 5–6 runs to eight lines on a phone, which
            covers the garden the player just walked to and pushes the buttons
            below the panel's own scroll. This is the half that lands. */}
        <p className="td__scripture">
          “He is not here, for he has risen, just like he said. Come, see the place where the Lord
          was lying.”
        </p>
        <p className="td__ref">Matthew 28:6</p>
        <div className="td__tally">
          <div>
            <b>{lives}</b>
            <span>of {WALK_LIVES} left</span>
          </div>
          <div>
            <b>{score}</b>
            <span>score</span>
          </div>
          <div>
            <b>{Math.max(best, score)}</b>
            <span>best</span>
          </div>
        </div>
        <p className="td__narrate">The guard was still there. It made no difference at all.</p>
        {gate ? (
          <div className="td__gate">
            <p className="td__gate-lead">
              {score > best ? 'That is your best walk.' : `${score}.`} It lives in this browser only
              — clear it and it is gone.
            </p>
            <button
              type="button"
              className="btn btn--primary btn--block btn--lg"
              onClick={onNeedAccount}
            >
              Make a free account to keep playing
            </button>
            <p className="td__gate-note">
              Free, takes a moment. Keeps your best walk and carries it to your phone.
            </p>
            <button type="button" className="td__gate-more" onClick={onUpgrade}>
              Or see what Premium opens up
            </button>
          </div>
        ) : (
          <button type="button" className="btn btn--leaf btn--block btn--lg" onClick={begin}>
            Walk it again
          </button>
        )}
        <button type="button" className="btn btn--ghost btn--block" onClick={share}>
          <Icon name="share" size={15} />
          {copied ? 'Copied' : 'Share your walk'}
        </button>
        <button type="button" className="btn btn--ghost btn--block" onClick={onExit}>
          Back to practice
        </button>
      </>
    );
  } else {
    const just = asked?.question ?? round;
    panel = (
      <>
        <p className="td__eyebrow">Turned back</p>
        <h2 className="td__title">
          {at} of {WALK_STEPS} steps
        </h2>
        <p className="td__scripture td__scripture--small">{just.note}</p>
        <p className="td__ref">{just.ref}</p>
        <p className="td__narrate">The garden is still there. Go again.</p>
        <button type="button" className="btn btn--primary btn--block btn--lg" onClick={begin}>
          Set out again
        </button>
        <button type="button" className="btn btn--ghost btn--block" onClick={onExit}>
          Back to practice
        </button>
      </>
    );
  }

  /*
   * The corridor gets a fixed pane and the panel sits below it, rather than
   * the panel floating over a canvas that stretches to the section.
   *
   * Three arrangements were tried and two were wrong in ways worth recording.
   * A canvas pinned to a fraction of the viewport ignores the app's header,
   * nav and hymn dock, and pushed the fourth answer off the bottom of a phone.
   * A section sizing to its content instead resized the canvas every time the
   * panel changed height, so the horizon jumped between question and verse.
   * Stacking a fixed pane above a panel in flow was stable but needed more
   * height than the chrome leaves: measured, only a 230px corridor fitted.
   *
   * So while you are walking the panel sits *inside* the stage, over the
   * corridor: the two share the same vertical space instead of competing for
   * it, and the canvas never resizes between a question and a verse.
   *
   * The intro and the two endings are read-and-tap screens, not play screens,
   * and they carry far more than an overlay can hold — verse, tally, the
   * account ask, share, exit. Those sit below a shorter stage in normal flow,
   * where scrolling is expected and nothing gets clipped mid-word.
   */
  return (
    <section ref={sectionRef} className={`screen td${walking ? '' : ' td--rest'}`}>
      <div ref={stageRef} className="td__stage">
        <canvas ref={canvasRef} className="td__canvas" />
        {walking ? (
          <div className="td__hud">
            <button type="button" className="icon-btn" onClick={onExit} aria-label="Leave the walk">
              <Icon name="close" size={16} />
            </button>
            <div className="td__steps">
              {Array.from({ length: WALK_STEPS }, (_, i) => (
                <span key={i} className={`td__step${i < at ? ' is-done' : ''}`} />
              ))}
            </div>
            <div className="td__lives">
              {Array.from({ length: WALK_LIVES }, (_, i) => (
                <span key={i} className={`td__life${i < WALK_LIVES - lives ? ' is-gone' : ''}`} />
              ))}
            </div>
          </div>
        ) : null}
        {walking ? (
          <div ref={panelRef} className="td__panel">
            {panel}
          </div>
        ) : null}
      </div>
      {walking ? null : (
        <div ref={panelRef} className="td__panel td__panel--flow">
          {panel}
        </div>
      )}
    </section>
  );
}
